import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import services from '../../services'
import { useAuth } from '../../hooks/useAuth'
import { useNotification } from '../../hooks/useNotification.js'
import NovaSolicitacao from './NovaSolicitacao'
import SolicitacaoDetalhe from './SolicitacaoDetalhe'
import { exportarSolicitacoes } from './exportar'
import { useBuscaNasConversas } from './useBuscaNasConversas'
import {
  atrasada, dataDoPrazo, dataHora, Detalhe, encerrado, filtrar, PRIORIDADES, Resumo, rotuloPrioridade, rotuloStatus, rotuloTipo, STATUS, STATUS_EM_ABERTO, TIPOS
} from './comum'
import '../gestao/gestao.scss'
import './solicitacoes.scss'

const TAMANHO = 25

type Escopo = 'minhas' | 'compartilhadas' | 'todas'

/** Solicitações ao time de BI: as minhas e, para quem atende, todas. */
export default function Solicitacoes() {
  const { getUserRoles } = useAuth()
  const { notify } = useNotification()
  const [params, setParams] = useSearchParams()
  const roles: string[] = getUserRoles() || []
  const podeAbrir = roles.includes('solicitacoes')
  const gestao = roles.includes('gestao_solicitacoes') || roles.includes('bi_dashboards_read_any')

  // As três visões: o que eu pedi, o que outras pessoas compartilharam comigo e tudo (gestão).
  const [escopo, setEscopo] = useState<Escopo>(params.get('aba') === 'compartilhadas' && podeAbrir ? 'compartilhadas' : gestao && !podeAbrir ? 'todas' : 'minhas')
  const todas = escopo === 'todas'
  const compartilhadas = escopo === 'compartilhadas'
  const [lista, setLista] = useState<Resumo[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [tipo, setTipo] = useState('')
  // Abre sem o que já terminou; "Todos os status" traz de volta as concluídas e canceladas.
  const [status, setStatus] = useState(STATUS_EM_ABERTO)
  const [prioridade, setPrioridade] = useState('')
  const [soAtrasadas, setSoAtrasadas] = useState(false)
  const [responsavel, setResponsavel] = useState('')
  const [pagina, setPagina] = useState(1)
  const [nova, setNova] = useState(false)

  const aberta = Number(params.get('abrir')) || null

  const carregar = useCallback(async () => {
    const r = compartilhadas ? await services.api.solicitacoesListar(false, undefined, true) : await services.api.solicitacoesListar(todas)
    if (r.fail) { setErro(r.error || 'Não foi possível carregar as solicitações.'); return }
    setErro(null)
    setLista(r.data)
  }, [todas, compartilhadas])

  useEffect(() => { setLista(null); carregar() }, [carregar])
  useEffect(() => { setPagina(1) }, [busca, tipo, status, prioridade, soAtrasadas, responsavel, escopo])

  useEffect(() => { setResponsavel('') }, [todas])

  const nasConversas = useBuscaNasConversas(busca, todas, compartilhadas)
  const visiveis = useMemo(() => filtrar(lista || [], { busca, tipo, status, prioridade, soAtrasadas, responsavel, nasConversas }), [lista, busca, tipo, status, prioridade, soAtrasadas, responsavel, nasConversas])
  // Quem tem solicitações atribuídas na lista: para filtrar por responsável (só na visão de todas).
  const responsaveis = useMemo(() => {
    const mapa = new Map<number, string>()
    ;(lista || []).forEach(s => { if (s.responsavelId != null) mapa.set(s.responsavelId, s.responsavelNome || `Pessoa #${s.responsavelId}`) })
    return Array.from(mapa.entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
  }, [lista])
  const totalPaginas = Math.max(1, Math.ceil(visiveis.length / TAMANHO))
  const daPagina = visiveis.slice((pagina - 1) * TAMANHO, pagina * TAMANHO)

  const abas: { id: Escopo; rotulo: string }[] = [
    ...(podeAbrir ? [{ id: 'minhas' as Escopo, rotulo: 'Minhas' }] : []),
    ...(podeAbrir ? [{ id: 'compartilhadas' as Escopo, rotulo: 'Compartilhadas comigo' }] : []),
    ...(gestao ? [{ id: 'todas' as Escopo, rotulo: 'Todas' }] : [])
  ]
  const mostraSolicitante = escopo !== 'minhas'

  const abrir = (id: number) => setParams({ abrir: String(id) })
  const fechar = () => { setParams({}); carregar() }

  const criada = (c: Detalhe, falhas: string[]) => {
    setNova(false)
    notify(`Solicitação #${c.id} aberta.`, { variant: 'success' })
    if (falhas.length) notify(`A solicitação foi criada, mas estes anexos não subiram: ${falhas.join(', ')}. Anexe de novo pelos detalhes.`, { variant: 'warning' })
    carregar()
    abrir(c.id)
  }

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Solicitações</h1>
        <div className="ng-acoes">
          <button type="button" className="ng-btn" disabled={visiveis.length === 0} onClick={() => exportarSolicitacoes(visiveis)} title="Baixa a lista que está na tela como planilha, que abre no Excel"><DownloadIcon fontSize="small" /> Exportar Excel</button>
          {podeAbrir && <button type="button" className="ng-btn prim" onClick={() => setNova(true)}><AddIcon fontSize="small" /> Nova solicitação</button>}
        </div>
      </div>
      <p className="ng-explic">
        Peça ao time de BI cadastro, acesso, dashboard novo ou alteração, e acompanhe por aqui, sozinho ou junto de quem você compartilhar. Você é avisado no sino a cada novidade.
      </p>

      {abas.length > 1 && (
        <div className="sol-abas" role="tablist">
          {abas.map(a => (
            <button key={a.id} type="button" role="tab" aria-selected={escopo === a.id} className={escopo === a.id ? 'on' : ''} onClick={() => setEscopo(a.id)}>{a.rotulo}</button>
          ))}
        </div>
      )}

      <div className="ng-filtros">
        <label className="ng-busca"><SearchIcon fontSize="small" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por número, título, pessoa ou texto da conversa" aria-label="Buscar solicitação" />
        </label>
        <select className="ng-select" value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Todos os tipos</option>{TIPOS.map(t => <option key={t.id} value={t.id}>{t.rotulo}</option>)}
        </select>
        <select className="ng-select" value={status} onChange={e => setStatus(e.target.value)} aria-label="Status">
          <option value={STATUS_EM_ABERTO}>Em aberto</option><option value="">Todos os status</option>{Object.keys(STATUS).map(s => <option key={s} value={s}>{rotuloStatus(s)}</option>)}
        </select>
        <select className="ng-select" value={prioridade} onChange={e => setPrioridade(e.target.value)} aria-label="Prioridade">
          <option value="">Todas as prioridades</option>{PRIORIDADES.map(p => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
        </select>
        {todas && (
          <select className="ng-select" value={responsavel} onChange={e => setResponsavel(e.target.value)} aria-label="Responsável">
            <option value="">Todos os responsáveis</option>
            <option value="nenhum">Sem responsável</option>
            {responsaveis.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </select>
        )}
        <label className="sol-check"><input type="checkbox" checked={soAtrasadas} onChange={e => setSoAtrasadas(e.target.checked)} /> Só atrasadas</label>
      </div>

      <div className="ng-cartao" style={{ padding: '6px 10px' }}>
        <div className="ng-tabela">
          <table>
            <thead><tr>
              <th>Solicitação</th>{mostraSolicitante && <th>Solicitante</th>}<th>Status</th><th>Prioridade</th><th>Prazo</th><th>Responsável</th><th>Aberta em</th>
            </tr></thead>
            <tbody>
              {erro && <tr><td colSpan={mostraSolicitante ? 7 : 6} className="vazio">{erro}</td></tr>}
              {!lista && !erro && <tr><td colSpan={mostraSolicitante ? 7 : 6} className="vazio">Carregando…</td></tr>}
              {lista && visiveis.length === 0 && (
                <tr><td colSpan={mostraSolicitante ? 7 : 6} className="vazio">{lista.length === 0 ? (todas ? 'Ninguém abriu solicitação ainda.' : compartilhadas ? 'Ninguém compartilhou solicitação com você ainda.' : 'Você ainda não abriu nenhuma solicitação.') : status === STATUS_EM_ABERTO && lista.some(s => encerrado(s.status)) ? 'Nada em aberto com esses filtros. Escolha "Todos os status" para ver também as concluídas e canceladas.' : 'Nada com esses filtros.'}</td></tr>
              )}
              {daPagina.map(s => (
                <tr key={s.id} className="clicavel" tabIndex={0} onClick={() => abrir(s.id)} onKeyDown={e => { if (e.key === 'Enter') abrir(s.id) }}>
                  <td><b>#{s.id} · {s.titulo}</b><small style={{ display: 'block', color: '#8b92a1' }}>{rotuloTipo(s.tipo)}</small></td>
                  {mostraSolicitante && <td>{s.solicitanteNome || '—'}</td>}
                  <td><span className={`ng-pill ${STATUS[s.status]?.tom || ''}`}>{rotuloStatus(s.status)}</span>{s.bloqueado && <span className="ng-pill alerta" style={{ marginLeft: 6 }}>Bloqueada</span>}</td>
                  <td>{rotuloPrioridade(s.prioridade)}</td>
                  <td>{s.prazo ? <span className={atrasada(s) ? 'sol-atraso' : undefined}>{dataDoPrazo(s.prazo)}{atrasada(s) ? ' · atrasada' : ''}</span> : <span style={{ color: '#8b92a1' }}>—</span>}</td>
                  <td>{s.responsavelNome || <span style={{ color: '#8b92a1' }}>Ninguém ainda</span>}</td>
                  <td>{dataHora(s.criadoEm)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {visiveis.length > TAMANHO && (
          <div className="ng-paginas">
            <button type="button" className="ng-btn" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>Anterior</button>
            <span>Página {pagina} de {totalPaginas} · {visiveis.length} solicitações</span>
            <button type="button" className="ng-btn" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Próxima</button>
          </div>
        )}
      </div>

      <NovaSolicitacao aberto={nova} aoFechar={() => setNova(false)} aoCriar={criada} />
      <SolicitacaoDetalhe id={aberta} aoFechar={fechar} />
    </div>
  )
}
